import type { InternalQuery } from "@/features/analytics/query";
import type {
  AnalyticsQuery,
  AnalyticsQueryFilters,
  MetricName,
} from "schemas/generated/analyticsRead";

import { ConditionOperatorEnum, PageTypeEnum } from "schemas/analyticsRead";

export const RESULTS_PER_PAGE = 25;

const DEFAULT_SORT_METRIC: MetricName = "unique_sessions";
const DEFAULT_QUERY_RESULT_LIMIT = RESULTS_PER_PAGE;
const DEFAULT_QUERY_RESULT_OFFSET = 0;
const DEFAULT_ORDER: AnalyticsQuery["order"] = "DESC";

export const DEFAULT_METRICS: AnalyticsQuery["metrics"] = [
  "conversions",
  "views",
  "conversion_rates",
  "unique_sessions",
  "revenue",
  "revenue_per_session",
  "average_order_value",
];

export const DEFAULT_FILTERS: AnalyticsQueryFilters = {
  urlPath: [
    {
      operator: ConditionOperatorEnum.DOES_NOT_CONTAIN,
      value: "/checkouts/",
    },
    {
      operator: ConditionOperatorEnum.DOES_NOT_CONTAIN,
      value: "/orders/",
    },
    {
      operator: ConditionOperatorEnum.DOES_NOT_CONTAIN,
      value: "/tools/recurring/portal/",
    },
    {
      operator: ConditionOperatorEnum.DOES_NOT_CONTAIN,
      value: "/account/",
    },
  ],
  pageType: PageTypeEnum.ENTRY_PAGES,
  urlParams: {},
};

export const DEFAULT_QUERY: InternalQuery = {
  order: DEFAULT_ORDER,
  sortMetric: DEFAULT_SORT_METRIC,
  metrics: DEFAULT_METRICS,
  offset: DEFAULT_QUERY_RESULT_OFFSET,
  limit: DEFAULT_QUERY_RESULT_LIMIT,
};

export const OVERVIEW_TABLE_METRICS: {
  key: MetricName;
  label: string;
  tooltip?: string;
}[] = [
  {
    key: "unique_sessions",
    label: "Sessions",
    tooltip:
      "Number of visits to your site. Visits starts when a user lands on a page and end when no events are sent for 30 minutes.",
  },
  {
    key: "conversions",
    label: "Conversions",
    tooltip:
      "The number of sessions where a purchase occurred after this page was the first one viewed.",
  },
  {
    key: "revenue",
    label: "Revenue",
    tooltip:
      "The total value of purchases that occurred after this page was the first one viewed.",
  },
  {
    key: "conversion_rates",
    label: "Conversion Rate",
    tooltip:
      "The percentage of sessions where this page was the first visited and led to a purchase.",
  },
  {
    key: "average_order_value",
    label: "AOV",
    tooltip:
      "The average order value from sessions where this page was the first one viewed.",
  },
  {
    key: "revenue_per_session",
    label: "Revenue per Session",
    tooltip:
      "The average revenue generated per session where this page was the first one viewed.",
  },
];

export type TableMetric = MetricName;

export const METRICS_REQUIRING_DECIMALS: TableMetric[] = [
  "revenue",
  "revenue_per_session",
  "conversion_rates",
  "average_order_value",
];

export const METRICS_REQUIRING_PERCENTAGE: TableMetric[] = ["conversion_rates"];
export const METRICS_REQUIRING_CURRENCY: TableMetric[] = [
  "revenue",
  "revenue_per_session",
  "average_order_value",
];

export const METRICS_ALLOWING_ZERO_VALUE_WHEN_NO_DATA: TableMetric[] = [
  "revenue",
  "unique_sessions",
  "conversions",
];

export const DETAILS_PAGE_CARD_METRICS: {
  label: string;
  key: MetricName;
}[] = [
  { label: "Conversion Rate", key: "conversion_rates" },
  { label: "Revenue/Session", key: "revenue_per_session" },
  { label: "Average Order Value", key: "average_order_value" },
  { label: "Total Sessions", key: "unique_sessions" },
  { label: "Total Conversions", key: "conversions" },
  { label: "Total Revenue", key: "revenue" },
];
